import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import RichTextArea from '@/components/RichTextArea';
import { API } from '@/App';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Mail, Send, AlertTriangle, CalendarIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useEmailSendConfirmation } from '@/hooks/useEmailSendConfirmation';
import { SenderNamePicker } from './ThankYouEmailPanel';

const formatConfirmationDate = (isoStr) => {
  if (!isoStr) return '';
  const [y, m, d] = isoStr.split('-').map((s) => parseInt(s, 10));
  if (!y || !m || !d) return isoStr;
  const dt = new Date(y, m - 1, d);
  const weekday = dt.toLocaleDateString('en-NZ', { weekday: 'long' });
  const month = dt.toLocaleDateString('en-NZ', { month: 'long' });
  return `${weekday}, ${d} ${month} ${y}`;
};

const isoFromDate = (dt) => {
  if (!dt) return null;
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const d = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/**
 * Shared dialog for composing + sending any covering email
 * (Entry Confirmation, Draft Programme, Final Programme, etc.).
 *
 * Each consumer points at its own backend endpoint base — the backend
 * exposes the same five routes for every email type:
 *   GET    /{endpointBase}/{competitionId}
 *   PUT    /{endpointBase}/{competitionId}
 *   GET    /{endpointBase}/{competitionId}/preflight
 *   GET    /{endpointBase}/{competitionId}/sender-options
 *   GET    /{endpointBase}/{competitionId}/default-body
 *   POST   /{endpointBase}/{competitionId}/send
 *
 * Props:
 *   open, onOpenChange   — alert-dialog controls
 *   competitionId        — required once dialog opens
 *   clubId               — 'all' or a specific club id
 *   endpointBase         — e.g. 'entry-confirmation-email' or 'draft-programme-email'
 *   title                — dialog title (e.g. "Email Draft Programme (Print)")
 *   description          — short helper text under the title
 *   testIdPrefix         — used for every data-testid in this dialog instance
 *   extraSendBody        — extra fields merged into the POST send payload
 *                          (e.g. { version: 'print' })
 */
export default function CoveringEmailDialog({
  open,
  onOpenChange,
  competitionId,
  clubId,
  endpointBase,
  title,
  description,
  testIdPrefix,
  extraSendBody = {},
}) {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [settings, setSettings] = useState(null);
  const [preflight, setPreflight] = useState(null);
  const [senderOptions, setSenderOptions] = useState({ default: '', options: [] });
  const { gate, ConfirmDialogs } = useEmailSendConfirmation();

  useEffect(() => {
    if (!open || !competitionId) return;
    setLoading(true);
    (async () => {
      try {
        const [s, pf, snd] = await Promise.all([
          axios.get(`${API}/${endpointBase}/${competitionId}`, { withCredentials: true }),
          axios.get(`${API}/${endpointBase}/${competitionId}/preflight`, {
            withCredentials: true,
            params: clubId && clubId !== 'all' ? { club_id: clubId } : {},
          }),
          axios.get(`${API}/${endpointBase}/${competitionId}/sender-options`, { withCredentials: true }),
        ]);
        setSettings(s.data);
        setPreflight(pf.data);
        setSenderOptions(snd.data || { default: '', options: [] });
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to load email settings');
        onOpenChange(false);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, competitionId, clubId, endpointBase]);

  const update = (patch) => setSettings(prev => ({ ...(prev || {}), ...patch }));

  const persistSettings = async () => {
    if (!competitionId || !settings) return;
    try {
      await axios.put(
        `${API}/${endpointBase}/${competitionId}`,
        {
          greeting: settings.greeting || '',
          subject: settings.subject || '',
          body_html: settings.body_html || '',
          acknowledgement: settings.acknowledgement || '',
          sender_key: settings.sender_key || null,
          confirmation_date: settings.confirmation_date || null,
        },
        { withCredentials: true }
      );
    } catch (err) {
      console.warn('Could not persist email settings:', err);
    }
  };

  const runSend = async (live) => {
    setSending(true);
    try {
      await persistSettings();
      const body = { club_id: clubId || 'all', live, ...extraSendBody };
      const res = await axios.post(
        `${API}/${endpointBase}/${competitionId}/send`,
        body,
        { withCredentials: true, timeout: 180000 }
      );
      const sentCount = (res.data.sent || []).length;
      const skippedCount = (res.data.skipped || []).length;
      if (sentCount) toast.success(`${sentCount} email${sentCount === 1 ? '' : 's'} ${live ? 'sent' : 'sent (dev mode → failsafe)'}`);
      if (skippedCount) toast.warning(`${skippedCount} skipped — see console for reasons`);
      if (skippedCount) console.warn('Skipped:', res.data.skipped);
    } catch (err) {
      const status = err.response?.status;
      let msg;
      if (status === 502 || status === 504 || err.code === 'ECONNABORTED') {
        msg = 'Server is still processing the send — check your inbox in a minute or refresh and look at the email log.';
      } else {
        msg = err.response?.data?.detail || err.message || 'Send failed';
      }
      toast.error(msg);
      console.error(`${endpointBase} send failed:`, err);
    } finally {
      setSending(false);
      onOpenChange(false);
    }
  };

  const handleSendClick = () => { gate((live) => { runSend(live); }); };

  const missingEmailClubs = useMemo(
    () => (preflight?.targets || []).filter(t => t.missing_email),
    [preflight]
  );
  const targetCount = (preflight?.targets || []).length;
  const deliverableCount = (preflight?.targets || []).filter(t => !t.missing_email).length;

  const tid = (suffix) => `${testIdPrefix}-${suffix}`;

  return (
    <>
      <AlertDialog open={open} onOpenChange={onOpenChange}>
        <AlertDialogContent
          className="max-w-3xl max-h-[90vh] overflow-y-auto"
          data-testid={tid('dialog')}
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-[#5C4033]" />
              {title}
            </AlertDialogTitle>
            <AlertDialogDescription>{description}</AlertDialogDescription>
          </AlertDialogHeader>

          {loading && <p className="text-sm text-stone-600">Loading…</p>}

          {!loading && settings && preflight && (
            <div className="space-y-5 mt-2">
              <div>
                <Label>Email Subject</Label>
                <p className="text-xs text-stone-500 mb-1">
                  Use <code>[[Club Name]]</code> to include the recipient club's name.
                </p>
                <input
                  type="text"
                  className="w-full border rounded-lg px-3 py-2 mt-1"
                  value={settings.subject || ''}
                  onChange={(e) => update({ subject: e.target.value })}
                  data-testid={tid('subject-input')}
                />
              </div>

              <div className="rounded-lg border bg-amber-50/60 border-amber-200 px-3 py-2 text-sm text-amber-900">
                <p>
                  <span className="font-semibold">{deliverableCount}</span> club{deliverableCount === 1 ? '' : 's'} will receive an email.
                  {missingEmailClubs.length > 0 && (
                    <> <AlertTriangle className="inline h-4 w-4 text-amber-700 mb-0.5" />{' '}
                      <span className="font-semibold">{missingEmailClubs.length}</span> skipped (no email on file):
                      {' '}{missingEmailClubs.map(c => c.name).join(', ')}
                    </>
                  )}
                </p>
                <div className="mt-1 flex items-center gap-2 text-xs">
                  <span className="font-semibold">Confirmation Date:</span>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-7 px-2 py-1 text-xs font-normal border-amber-300 bg-white hover:bg-amber-50"
                        data-testid={tid('date-picker-trigger')}
                      >
                        <CalendarIcon className="h-3.5 w-3.5 mr-1.5 text-amber-700" />
                        {formatConfirmationDate(settings.confirmation_date)
                          || preflight.confirmation_date_text}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      align="start"
                      className="w-auto p-0 z-[120]"
                      data-testid={tid('date-picker-popover')}
                    >
                      <Calendar
                        mode="single"
                        selected={settings.confirmation_date
                          ? new Date(settings.confirmation_date + 'T00:00:00')
                          : null}
                        onSelect={(d) => {
                          if (d) update({ confirmation_date: isoFromDate(d) });
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              <div>
                <Label>Greeting</Label>
                <input
                  type="text"
                  className="w-full border rounded-lg px-3 py-2 mt-1"
                  value={settings.greeting || ''}
                  onChange={(e) => update({ greeting: e.target.value })}
                  data-testid={tid('greeting-input')}
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label>Body text of the email</Label>
                  <button
                    type="button"
                    className="text-xs text-stone-600 underline hover:text-stone-800"
                    onClick={async () => {
                      try {
                        const res = await axios.get(
                          `${API}/${endpointBase}/${competitionId}/default-body`,
                          { withCredentials: true }
                        );
                        update({ body_html: res.data.body_html });
                        toast.success('Body reset to default');
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Failed to load default text');
                      }
                    }}
                    data-testid={tid('reset-body-btn')}
                  >
                    Reset Default Text
                  </button>
                </div>
                <p className="text-xs text-stone-500 mb-1">
                  Variables are pre-filled with actual values — for example{' '}
                  <code>[[Confirmation Date]]</code>. If you've altered these and
                  wish to reset, please click the "Reset Default Text" link at the right.
                </p>
                <RichTextArea
                  value={settings.body_html || ''}
                  onChange={(html) => update({ body_html: html })}
                  testId={tid('body-editor')}
                />
              </div>

              <div>
                <Label>Acknowledgement</Label>
                <input
                  type="text"
                  className="w-full border rounded-lg px-3 py-2 mt-1"
                  value={settings.acknowledgement || ''}
                  onChange={(e) => update({ acknowledgement: e.target.value })}
                  data-testid={tid('acknowledgement-input')}
                />
              </div>

              <SenderNamePicker
                options={senderOptions}
                value={settings.sender_key}
                onChange={(key) => update({ sender_key: key })}
                testIdPrefix={testIdPrefix}
              />
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel data-testid={tid('close-btn')}>Close</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleSendClick(); }}
              disabled={sending || loading || targetCount === 0}
              className="bg-[#5C4033] hover:bg-[#3d2a22] text-white"
              data-testid={tid('send-btn')}
            >
              <Send className="mr-2 h-4 w-4" />
              {sending ? 'Sending…' : 'Send Emails'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ConfirmDialogs />
    </>
  );
}
